// utils/elementFormat/color.js
//
// How a `color` element NORMALISES and which values it REFUSES (keepr-web
// docs/plans/data-types-second-pass.md §4.1, decision D11): one rule for every
// write path — the form, POST/PUT /api/items, bulk, batch ingest, multi add,
// quick add, a public form and the automation actions.
//
// PURE AND DEPENDENCY-FREE ON PURPOSE, and vendored byte-identical into
// keepr-web at `src/utils/elementFormat/color.js`, so the form's picker and
// the API's 400 `invalid_color` can never disagree about what a color is.
// MIRRORED in keepr-web — change them together, with the shared vectors in
// `vectors.json` beside this file. Nothing here may require() anything.
//
// THE RULE:
//
//   stored    a lower-case "#rrggbb" — one spelling per color, so a filter,
//             a sort and an export meet the same seven characters however
//             the color was entered.
//   accepted  "#rrggbb" and "#rgb" in either case; "rgb(r, g, b)" with three
//             whole numbers from 0 to 255, separated by commas or spaces; and
//             the 22 colors a choice may carry (keepr-web src/utils/dictionary.js
//             `elementColors`) by their CSS name, in any case, spaces ignored
//             ("DodgerBlue", "dodger blue"). Surrounding space is trimmed.
//   refused   everything else — and on purpose: a hex without its "#" (a word
//             like "facade" or "bad" would otherwise be a color); a
//             translucent color ("#rrggbbaa", "rgba(…)", "rgb(… / 0.5)"),
//             because keepr stores solid colors and dropping the alpha would
//             change the color; percentages; any CSS name outside the 22 (the
//             148 CSS names disagree with what the picker calls "Blue" or
//             "Orange", and a short list everyone can see beats a long one
//             nobody reads); anything longer than MAX_INPUT characters.
//
// The ECHO rule is the caller's, not this module's: a value identical to the
// one already stored always saves (utils/typedElements.js), so a text element
// turned into a color never bricks an item that still holds "navy".

'use strict';

// Longer than any spelling above ("rgb( 255 , 255 , 255 )" is 23); bounds
// the work before a regex runs.
const MAX_INPUT = 64;

// The 22 colors a choice may carry, by CSS name (lower-case, no spaces), with
// their CSS Color Module values. keepr-web holds the list a person picks from
// (src/utils/dictionary.js `elementColors`); a test there pins the two to the
// same names.
const NAMED_COLORS = Object.freeze({
	olive: '#808000',
	forestgreen: '#228b22',
	limegreen: '#32cd32',
	palegreen: '#98fb98',
	gold: '#ffd700',
	darkorange: '#ff8c00',
	tomato: '#ff6347',
	crimson: '#dc143c',
	deeppink: '#ff1493',
	violet: '#ee82ee',
	darkmagenta: '#8b008b',
	paleturquoise: '#afeeee',
	dodgerblue: '#1e90ff',
	darkcyan: '#008b8b',
	aqua: '#00ffff',
	wheat: '#f5deb3',
	tan: '#d2b48c',
	sandybrown: '#f4a460',
	chocolate: '#d2691e',
	saddlebrown: '#8b4513',
	lightgray: '#d3d3d3',
	darkgray: '#a9a9a9',
});

const HEX6 = /^#([0-9a-f]{6})$/i;
const HEX3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i;
// "rgb(" then three whole numbers of at most three digits, separated by a
// comma (spaces around it allowed) or by spaces alone, then ")".
const RGB = /^rgb\(\s*(\d{1,3})(?:\s*,\s*|\s+)(\d{1,3})(?:\s*,\s*|\s+)(\d{1,3})\s*\)$/i;
// The stored spelling, exactly.
const STORED = /^#[0-9a-f]{6}$/;

const toStr = v => (v === null || v === undefined ? '' : String(v));
const byte = n => (n < 16 ? '0' : '') + n.toString(16);
const ok = value => ({ value, valid: true, reason: null });
const refused = () => ({ value: '', valid: false, reason: 'not_a_color' });

/**
 * normalizeColor(raw) -> { value, valid, reason }
 *
 *   value   the color as it will be STORED ("#1f6feb"), and '' whenever
 *           `valid` is false, so a caller that stores the result without
 *           checking cannot persist a refused value.
 *   valid   the value is a color the platform stores.
 *   reason  why it was refused, or null: 'empty' | 'type' | 'not_a_color'.
 *           The callers hang their own sentences on these tokens.
 *
 * A blank input is not valid HERE ('empty'): emptiness is the caller's
 * question, and an untouched field is not a malformed color.
 */
function normalizeColor(raw) {
	if (raw !== null && raw !== undefined && typeof raw !== 'string') return { value: '', valid: false, reason: 'type' };
	const text = toStr(raw).trim();
	if (!text) return { value: '', valid: false, reason: 'empty' };
	if (text.length > MAX_INPUT) return refused();
	let m = HEX6.exec(text);
	if (m) return ok(`#${m[1].toLowerCase()}`);
	m = HEX3.exec(text);
	if (m) return ok(`#${m[1]}${m[1]}${m[2]}${m[2]}${m[3]}${m[3]}`.toLowerCase());
	m = RGB.exec(text);
	if (m) {
		const parts = [m[1], m[2], m[3]].map(Number);
		if (parts.some(n => n > 255)) return refused();
		return ok(`#${parts.map(byte).join('')}`);
	}
	// Own properties only: "constructor" or "__proto__" is not a color name.
	const name = text.replace(/\s+/g, '').toLowerCase();
	if (Object.prototype.hasOwnProperty.call(NAMED_COLORS, name)) return ok(NAMED_COLORS[name]);
	return refused();
}

/** True when `raw` is a color this platform will store. */
function isValidColor(raw) {
	return normalizeColor(raw).valid;
}

/** True when `value` is already in the stored spelling ("#1f6feb"). */
function isStoredColor(value) {
	return typeof value === 'string' && STORED.test(value);
}

module.exports = {
	MAX_INPUT,
	NAMED_COLORS,
	normalizeColor,
	isValidColor,
	isStoredColor,
};
